import React, { useRef, useState } from "react";
import {
    Spinner,
    SpinnerSize,
    PrimaryButton,
    DefaultButton,
    IconButton,
    Dialog,
    DialogType,
    DialogFooter,
    Stack,
    Text,
    Icon,
    ProgressIndicator,
    TextField,
    Dropdown,
    IDropdownOption
} from "@fluentui/react";
import { Document, RagStore } from "../../ata/types";
import { UploadJobProgress } from "../../../services/ataService";
import styles from "./AtaDocumentList.module.css";

interface DocView {
    title: string;
    url?: string;
    page?: number;
}

interface AtaDocumentListProps {
    selectedStore: RagStore | null;
    documents: Document[];
    isLoading: boolean;
    processingFile?: string | null;
    uploadProgress?: UploadJobProgress | null;
    isDev?: boolean;
    availableStores: RagStore[];
    onUpload: (file: File) => void;
    onDelete: (doc: Document) => void;
    onSaveNote: (doc: Document, note: string) => Promise<void>;
    onMove: (doc: Document, targetStoreDisplayName: string) => Promise<void>;
}

function formatState(state: string): { text: string; color: string } {
    switch (state) {
        case "ACTIVE":
        case "DocumentState.STATE_ACTIVE":
        case "STATE_ACTIVE":
            return { text: "Actif", color: "#107c10" };
        case "PROCESSING":
        case "DocumentState.STATE_PROCESSING":
        case "STATE_PROCESSING":
            return { text: "En cours…", color: "#8a8886" };
        case "FAILED":
        case "DocumentState.STATE_FAILED":
        case "STATE_FAILED":
            return { text: "Erreur", color: "#a4262c" };
        default:
            return { text: state, color: "#605e5c" };
    }
}

function formatSize(bytes: number): string {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / 1024 / 1024).toFixed(2)} Mo`;
}

const AtaDocumentList: React.FC<AtaDocumentListProps> = ({
    selectedStore,
    documents,
    isLoading,
    processingFile,
    uploadProgress,
    isDev,
    availableStores,
    onUpload,
    onDelete,
    onSaveNote,
    onMove
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [deleteTarget, setDeleteTarget] = useState<Document | null>(null);
    const [overwriteTarget, setOverwriteTarget] = useState<File | null>(null);
    const [docView, setDocView] = useState<DocView | null>(null);
    const [noteTarget, setNoteTarget] = useState<Document | null>(null);
    const [noteDraft, setNoteDraft] = useState("");
    const [savingNote, setSavingNote] = useState(false);
    const [moveTarget, setMoveTarget] = useState<Document | null>(null);
    const [moveDestination, setMoveDestination] = useState<string | undefined>(undefined);
    const [movingDocument, setMovingDocument] = useState(false);

    const moveOptions: IDropdownOption[] = availableStores
        .filter(s => (s.displayName || "").toLowerCase() !== (selectedStore?.displayName || "").toLowerCase())
        .map(s => ({ key: s.displayName, text: s.displayName }));

    const progressPct = typeof uploadProgress?.progressPct === "number" ? uploadProgress.progressPct : undefined;
    const progressStage = uploadProgress?.stage || "processing";
    const pageProgressText =
        typeof uploadProgress?.pagesDone === "number" && typeof uploadProgress?.totalPages === "number"
            ? `${uploadProgress.pagesDone}/${uploadProgress.totalPages} pages`
            : undefined;
    const progressDescription = [`Etape: ${progressStage}`, pageProgressText, progressPct !== undefined ? `${progressPct}%` : undefined]
        .filter(Boolean)
        .join(" • ");

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const exists = documents.some(d => (d.displayName || "").toLowerCase() === file.name.toLowerCase());
            if (exists) {
                setOverwriteTarget(file);
            } else {
                onUpload(file);
            }
        }
        // Reset so same file can be re-uploaded
        e.target.value = "";
    };

    if (!selectedStore) {
        return (
            <div className={styles.emptyState}>
                <Icon iconName="DocumentSet" className={styles.emptyIcon} />
                <Text className={styles.emptyTitle}>Sélectionnez une catégorie</Text>
                <Text className={styles.emptySubtitle}>Choisissez une catégorie dans la liste de gauche pour afficher et gérer ses documents.</Text>
            </div>
        );
    }

    if (selectedStore.storeExists === false) {
        return (
            <div className={styles.container}>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center" className={styles.header}>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                        <Icon iconName="Warning" style={{ color: "#ca5010", fontSize: 18 }} />
                        <Text variant="large" className={styles.title}>
                            {selectedStore.displayName}
                        </Text>
                        <span
                            style={{
                                fontSize: 11,
                                color: "#ca5010",
                                background: "#fde7d0",
                                borderRadius: 4,
                                padding: "2px 7px"
                            }}
                        >
                            Non initialis&eacute;
                        </span>
                    </Stack>
                    <input
                        ref={fileInputRef}
                        type="file"
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx,.txt,.xlsx,.csv,.png,.jpg,.jpeg"
                    />
                    {(isDev || true) && (
                        <PrimaryButton
                            iconProps={{ iconName: "Upload" }}
                            text={processingFile ? "Envoi en cours\u2026" : "Ajouter un document"}
                            disabled={!!processingFile}
                            onClick={() => fileInputRef.current?.click()}
                        />
                    )}
                </Stack>
                <div className={styles.emptyState} style={{ paddingTop: 32 }}>
                    <Icon iconName="CloudUpload" className={styles.emptyIcon} style={{ color: "#ca5010" }} />
                    <Text className={styles.emptyTitle}>Cat&eacute;gorie vide</Text>
                    <Text className={styles.emptySubtitle}>
                        Aucun store Google RAG n'existe encore pour &ldquo;{selectedStore.displayName}&rdquo;.
                        <br />
                        Uploadez un premier document pour initialiser automatiquement la cat&eacute;gorie.
                    </Text>
                    {processingFile && (
                        <ProgressIndicator
                            label={`Traitement de ${processingFile}\u2026`}
                            description={progressDescription || "Extraction OCR et initialisation du store en cours"}
                            percentComplete={progressPct !== undefined ? Math.max(0, Math.min(1, progressPct / 100)) : undefined}
                            className={styles.progress}
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" className={styles.header}>
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <Icon iconName="OpenFolderHorizontal" className={styles.headerIcon} />
                    <Text variant="large" className={styles.title}>
                        {selectedStore.displayName}
                    </Text>
                </Stack>
                <PrimaryButton
                    iconProps={{ iconName: "Upload" }}
                    text={processingFile ? "Traitement en cours…" : "Ajouter un document"}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!!processingFile}
                    styles={{
                        root: { borderRadius: 4 }
                    }}
                />
                <input type="file" ref={fileInputRef} style={{ display: "none" }} accept=".pdf,.txt,.docx,.xlsx,.png,.jpg,.jpeg" onChange={handleFileChange} />
            </Stack>

            {processingFile && (
                <ProgressIndicator
                    label={`Traitement de ${processingFile}…`}
                    description={progressDescription || "Extraction OCR en cours"}
                    percentComplete={progressPct !== undefined ? Math.max(0, Math.min(1, progressPct / 100)) : undefined}
                    className={styles.progressBar}
                    styles={{
                        root: { padding: "4px 16px 0" }
                    }}
                />
            )}

            {isLoading && documents.length === 0 ? (
                <div className={styles.centered}>
                    <Spinner size={SpinnerSize.medium} label="Chargement des documents…" />
                </div>
            ) : documents.length === 0 ? (
                <div className={styles.centered}>
                    <Icon iconName="PageList" className={styles.emptyItemIcon} />
                    <Text className={styles.emptyText}>Aucun document dans cette catégorie</Text>
                    <Text className={styles.emptySubtext}>Cliquez sur « Ajouter un document » pour ajouter un fichier PDF, TXT, DOCX…</Text>
                </div>
            ) : (
                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th className={styles.th}>Nom du fichier</th>
                                <th className={styles.th}>État</th>
                                <th className={styles.th}>Taille</th>
                                <th className={styles.th}>Type</th>
                                <th className={styles.th}>Notes</th>
                                <th className={styles.th}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {documents.map(doc => {
                                const stateInfo = formatState((doc as any).state || "");
                                return (
                                    <tr key={doc.name} className={styles.row}>
                                        <td className={styles.td}>
                                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                                <Icon iconName="TextDocument" className={styles.docIcon} />
                                                <button
                                                    className={styles.docNameBtn}
                                                    title={`Afficher ${doc.displayName}`}
                                                    onClick={() =>
                                                        setDocView({
                                                            title: doc.displayName,
                                                            url: doc.blobPath ? `/content/${doc.blobPath}` : undefined
                                                        })
                                                    }
                                                >
                                                    {doc.displayName}
                                                </button>
                                            </Stack>
                                        </td>
                                        <td className={styles.td}>
                                            <span className={styles.stateBadge} style={{ color: stateInfo.color }}>
                                                {stateInfo.text}
                                            </span>
                                        </td>
                                        <td className={styles.td}>
                                            <span className={styles.meta}>{(doc as any).sizeBytes ? formatSize((doc as any).sizeBytes) : "—"}</span>
                                        </td>
                                        <td className={styles.td}>
                                            <span className={styles.meta}>{(doc as any).mimeType || "—"}</span>
                                        </td>
                                        <td className={styles.td}>
                                            {doc.note ? (
                                                <span className={styles.meta} title={doc.note}>
                                                    {doc.note.length > 80 ? doc.note.slice(0, 80) + "…" : doc.note}
                                                </span>
                                            ) : (
                                                <span className={styles.meta}>Aucune note</span>
                                            )}
                                        </td>
                                        <td className={`${styles.td} ${styles.actionCell}`}>
                                            <IconButton
                                                iconProps={{ iconName: "MoveToFolder" }}
                                                title={`Déplacer ${doc.displayName} vers une autre catégorie`}
                                                onClick={() => {
                                                    setMoveTarget(doc);
                                                    setMoveDestination(undefined);
                                                }}
                                                styles={{ root: { color: "#605e5c" } }}
                                            />
                                            <IconButton
                                                iconProps={{ iconName: "Edit" }}
                                                title={`Modifier la note de ${doc.displayName}`}
                                                onClick={() => {
                                                    setNoteTarget(doc);
                                                    setNoteDraft(doc.note || "");
                                                }}
                                                styles={{ root: { color: "#0078d4" } }}
                                            />
                                            <IconButton
                                                iconProps={{ iconName: "Delete" }}
                                                title={`Supprimer ${doc.displayName}`}
                                                onClick={() => setDeleteTarget(doc)}
                                                styles={{ root: { color: "#a4262c" } }}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Overwrite confirm dialog */}
            <Dialog
                hidden={!overwriteTarget}
                onDismiss={() => setOverwriteTarget(null)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: "Remplacer le document existant",
                    subText: `Le document « ${overwriteTarget?.name} » est déjà présent dans cette catégorie. Voulez-vous le remplacer par la nouvelle version ?`
                }}
                minWidth={400}
            >
                <DialogFooter>
                    <PrimaryButton
                        text="Remplacer"
                        onClick={() => {
                            if (overwriteTarget) onUpload(overwriteTarget);
                            setOverwriteTarget(null);
                        }}
                    />
                    <DefaultButton text="Annuler" onClick={() => setOverwriteTarget(null)} />
                </DialogFooter>
            </Dialog>

            {/* Move dialog */}
            <Dialog
                hidden={!moveTarget}
                onDismiss={() => {
                    if (!movingDocument) {
                        setMoveTarget(null);
                        setMoveDestination(undefined);
                    }
                }}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: "Déplacer le document",
                    subText: moveTarget ? `Document : ${moveTarget.displayName}` : ""
                }}
                minWidth={520}
            >
                {moveOptions.length === 0 ? (
                    <Text>Aucune autre catégorie disponible.</Text>
                ) : (
                    <Dropdown
                        label="Catégorie de destination"
                        selectedKey={moveDestination}
                        options={moveOptions}
                        onChange={(_, option) => setMoveDestination(option?.key as string)}
                        disabled={movingDocument}
                        placeholder="Sélectionner une catégorie"
                    />
                )}

                <DialogFooter>
                    <PrimaryButton
                        text={movingDocument ? "Déplacement…" : "Déplacer"}
                        disabled={movingDocument || !moveTarget || !moveDestination || moveOptions.length === 0}
                        onClick={async () => {
                            if (!moveTarget || !moveDestination) return;
                            setMovingDocument(true);
                            try {
                                await onMove(moveTarget, moveDestination);
                                setMoveTarget(null);
                                setMoveDestination(undefined);
                            } finally {
                                setMovingDocument(false);
                            }
                        }}
                    />
                    <DefaultButton
                        text="Annuler"
                        disabled={movingDocument}
                        onClick={() => {
                            setMoveTarget(null);
                            setMoveDestination(undefined);
                        }}
                    />
                </DialogFooter>
            </Dialog>

            {/* Notes dialog */}
            <Dialog
                hidden={!noteTarget}
                onDismiss={() => {
                    if (!savingNote) setNoteTarget(null);
                }}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: `Notes du document`,
                    subText: noteTarget ? `Document : ${noteTarget.displayName}` : ""
                }}
                minWidth={560}
            >
                <TextField
                    label="Notes (intégrées dans l'index Google File Search)"
                    multiline
                    autoAdjustHeight
                    rows={6}
                    value={noteDraft}
                    onChange={(_, value) => setNoteDraft(value || "")}
                    disabled={savingNote}
                />
                <DialogFooter>
                    <PrimaryButton
                        text={savingNote ? "Enregistrement…" : "Enregistrer"}
                        disabled={savingNote}
                        onClick={async () => {
                            if (!noteTarget) return;
                            setSavingNote(true);
                            try {
                                await onSaveNote(noteTarget, noteDraft);
                                setNoteTarget(null);
                            } finally {
                                setSavingNote(false);
                            }
                        }}
                    />
                    <DefaultButton text="Annuler" disabled={savingNote} onClick={() => setNoteTarget(null)} />
                </DialogFooter>
            </Dialog>

            {/* Delete confirm dialog */}
            <Dialog
                hidden={!deleteTarget}
                onDismiss={() => setDeleteTarget(null)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: "Supprimer le document",
                    subText: `Êtes-vous sûr de vouloir supprimer « ${deleteTarget?.displayName} » ? Cette action est irréversible.`
                }}
                minWidth={400}
            >
                <DialogFooter>
                    <PrimaryButton
                        text="Supprimer"
                        onClick={() => {
                            if (deleteTarget) onDelete(deleteTarget);
                            setDeleteTarget(null);
                        }}
                        styles={{ root: { background: "#a4262c", border: "none" } }}
                    />
                    <DefaultButton text="Annuler" onClick={() => setDeleteTarget(null)} />
                </DialogFooter>
            </Dialog>

            {/* Document viewer overlay */}
            {docView && (
                <div className={styles.docViewerBackdrop} onClick={() => setDocView(null)}>
                    <div className={styles.docViewer} onClick={e => e.stopPropagation()}>
                        <div className={styles.docViewerHeader}>
                            <Icon iconName={docView.url ? "OpenFile" : "TextDocument"} className={styles.docViewerIcon} />
                            <span className={styles.docViewerTitle} title={docView.title}>
                                {docView.title}
                            </span>
                            {docView.page && <span className={styles.docViewerPage}>page&nbsp;{docView.page}</span>}
                            {docView.url && (
                                <a href={docView.url} target="_blank" rel="noreferrer" className={styles.docViewerNewTab} title="Ouvrir dans un nouvel onglet">
                                    <Icon iconName="OpenInNewTab" />
                                </a>
                            )}
                            <button className={styles.docViewerClose} onClick={() => setDocView(null)} title="Fermer">
                                <Icon iconName="Cancel" />
                            </button>
                        </div>
                        {docView.url ? (
                            <iframe src={docView.url} className={styles.docViewerFrame} title={docView.title} />
                        ) : (
                            <div className={styles.centered} style={{ flex: 1 }}>
                                <Icon iconName="PageHeaderEdit" style={{ fontSize: 48, color: "#c8c6c4", marginBottom: 16 }} />
                                <Text>Aperçu non disponible pour ce document.</Text>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AtaDocumentList;
